import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw, Save, Eye, PencilRuler } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { SectionCard } from "@/components/form-builder/SectionCard";
import { FieldDialog } from "@/components/form-builder/FieldDialog";
import { ProfileFields, EMPTY_PROFILE } from "@/components/member/ProfileFields";
import { AuthField } from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import * as F from "@/lib/formOps";
import { api, errMsg } from "@/lib/api";

const Preview = ({ form }) => {
  const [p, setP] = useState({ ...EMPTY_PROFILE });
  const slot = (id) => (label) => <AuthField label={label}><Input disabled placeholder={id === "pin" ? "MLS-GH-2024-XXXX" : "name@example.com"} className="h-11" /></AuthField>;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 sm:p-10" data-testid="builder-preview">
      <p className="text-xs text-gray-500 mb-6">Preview only. This is how members see the form when creating an account and on their profile. The password and security check are added automatically at the end.</p>
      <ProfileFields form={form} p={p} set={(x) => setP((s) => ({ ...s, ...x }))} slots={{ pin: slot("pin"), email: slot("email") }} />
    </div>
  );
};

const Tabs = ({ tab, setTab }) => (
  <div className="flex gap-1 p-1 bg-[#F3F4F6] rounded-lg mb-5 max-w-xs">
    {[["edit", "Edit", PencilRuler], ["preview", "Preview", Eye]].map(([k, l, Icon]) => (
      <button key={k} type="button" onClick={() => setTab(k)} data-testid={`builder-tab-${k}`}
        className={`flex-1 h-9 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${tab === k ? "bg-white shadow-sm text-[#111]" : "text-gray-500 hover:text-[#111]"}`}><Icon className="w-4 h-4" />{l}</button>
    ))}
  </div>
);

export default function FormBuilder() {
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [tab, setTab] = useState("edit");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/forms/member").then((r) => setForm(r.data)).catch(() => setFailed(true));
    api.get("/forms/member/meta").then((r) => setMeta(r.data)).catch(() => setFailed(true));
  }, []);
  const change = (next) => { setForm(next); setDirty(true); };
  if (failed) return <div className="p-10 text-center text-sm text-[#E60000] bg-white border rounded-lg" data-testid="builder-error">Could not load the account form. Please refresh the page.</div>;
  if (!form || !meta) return <div className="text-gray-500" data-testid="builder-loading">Loading form…</div>;

  const used = new Set(form.sections.flatMap((s) => s.fields.map((f) => f.key)));
  const pool = Object.entries(meta.builtins).filter(([k]) => !used.has(k)).map(([key, b]) => ({ key, ...b }));
  const ops = {
    rename: (si, title) => change(F.setSection(form, si, { title })),
    moveSection: (si, d) => change(F.moveSection(form, si, d)),
    removeSection: (si) => setConfirmation({ kind: "section", si }),
    moveField: (si, fi, d) => change(F.moveField(form, si, fi, d)),
    removeField: (si, fi) => change(F.removeField(form, si, fi)),
    edit: (si, field) => setDialog({ si, field }),
    addBuiltin: (si, b) => change(F.upsertField(form, si, { key: b.key, label: b.label, type: b.type, builtin: true, required: false, help: "", placeholder: "", options: [], width: "" })),
  };
  const save = async () => {
    if (form.sections.some((s) => !s.title.trim())) return toast.error("Every section needs a title");
    setBusy(true);
    try { const r = await api.put("/forms/member", form); setForm(r.data); setDirty(false); toast.success("Account form saved. Members see it straight away."); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const confirmChange = async () => {
    if (confirmation.kind === "section") change(F.removeSection(form, confirmation.si));
    else {
      const r = await api.post("/forms/member/reset");
      setForm(r.data); setDirty(false); toast.success("Form reset to default");
    }
  };

  return (
    <div data-testid="form-builder-page">
      <PageHeader eyebrow="Super Admin" title="Account Form Builder" description="Edit the form members fill in when they create an account and on their profile. Add, remove and reorder fields, and create sections. Core fields (locked) can be renamed and moved but not removed."
        actions={<>
          <BrandButton variant="outline" onClick={() => setConfirmation({ kind: "reset" })} disabled={busy} data-testid="builder-reset-button"><RotateCcw className="w-4 h-4" />Reset to default</BrandButton>
          <BrandButton onClick={save} disabled={busy || !dirty} data-testid="builder-save-button"><Save className="w-4 h-4" />{busy ? "Saving…" : dirty ? "Save form" : "Saved"}</BrandButton>
        </>} />
      {dirty && <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="builder-unsaved-banner">You have unsaved changes. Members will see them after you click Save form.</div>}
      <Tabs tab={tab} setTab={setTab} />
      {tab === "preview" ? <Preview form={form} /> : (
        <div className="space-y-4 max-w-4xl">
          {form.sections.map((s, si) => <SectionCard key={s.id} s={s} si={si} count={form.sections.length} locked={meta.locked} fixed={meta.fixed} pool={pool} ops={ops} />)}
          <BrandButton variant="dark" onClick={() => change(F.addSection(form, "New section"))} data-testid="builder-add-section"><Plus className="w-4 h-4" />Add section</BrandButton>
        </div>
      )}
      <ConfirmDialog open={!!confirmation} onClose={() => setConfirmation(null)} onConfirm={confirmChange}
        title={confirmation?.kind === "section" ? `Remove "${form.sections[confirmation.si]?.title}"?` : "Reset the account form?"}
        description={confirmation?.kind === "section" ? "Remove this section and its fields from the draft. Members only see the change after you save the form. Existing answers are kept." : "Restore the original GAMLS account form now. Custom fields disappear from the form, but answers already submitted are kept."}
        confirmLabel={confirmation?.kind === "section" ? "Remove section" : "Reset form"} testId={`builder-${confirmation?.kind || "reset"}`} />
      <FieldDialog state={dialog} sections={form.sections} locked={meta.locked} onClose={() => setDialog(null)}
        onSave={(si, field, orig) => { change(F.upsertField(form, si, field, orig)); setDialog(null); }} />
    </div>
  );
}
